<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { X, Search } from "lucide-vue-next"
import { Button } from "@/components/ui/button"

const props = defineProps<{
  open: boolean
  project: any
  users: any[]
  bookings: any[]
  projects: any[]
  resourceFilter: any
}>()

const emit = defineEmits<{
  apply: [individual: number[]]
  cancel: []
}>()

const localSelected = ref<number[]>([])
const checkedProjects = ref<Set<number>>(new Set()) // eigener Zustand der Projekt-Checkboxen
const search = ref('')

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    localSelected.value = [...(props.resourceFilter?.individual ?? [])]
    checkedProjects.value = new Set()
    search.value = ''
  }
})

const sortedFilteredUsers = computed(() => {
  const q = search.value.toLowerCase()
  return [...props.users]
    .filter(u => !q || `${u.firstname} ${u.lastname} ${u.short}`.toLowerCase().includes(q))
    .sort((a, b) => a.firstname.localeCompare(b.firstname, 'de'))
})

const projectsWithBookings = computed(() => {
  const ids = new Set(props.bookings.map((b: any) => b.project_id))
  // Nur aktive Projekte (status 0) anbieten, kein Urlaub/Spacer (id 0).
  return props.projects.filter((p: any) => ids.has(p.id) && p.status === 0 && p.id !== 0)
})

function todayIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// Enddatum als ISO-String ("yyyy-mm-dd") – die Buchungen kommen je nach Quelle
// aufbereitet (end = Date, oldend = ISO-String) oder roh (end = ISO-String) an.
function bookingEndIso(b: any): string {
  if (typeof b.oldend === 'string') return b.oldend
  if (typeof b.end === 'string') return b.end
  const d = b.end instanceof Date ? b.end : new Date(b.end)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`
}

function bookedUserIds(projectId: number, fromIso?: string): number[] {
  const ids = new Set<number>()
  for (const b of props.bookings) {
    if ((b as any).project_id !== projectId) continue
    // Buchung liegt komplett in der Vergangenheit -> überspringen
    if (fromIso && bookingEndIso(b) < fromIso) continue
    ids.add((b as any).user_id)
  }
  return [...ids]
}

function isProjectChecked(p: any): boolean {
  return checkedProjects.value.has(p.id)
}

function toggleProject(p: any) {
  const booked = bookedUserIds(p.id)
  const next = new Set(checkedProjects.value)
  if (next.has(p.id)) {
    // Abwählen: Personen dieses Projekts entfernen, aber die behalten, die ein
    // anderes noch angehaktes Projekt weiterhin braucht.
    next.delete(p.id)
    const keep = new Set<number>()
    for (const pid of next) for (const uid of bookedUserIds(pid)) keep.add(uid)
    localSelected.value = localSelected.value.filter(id => !booked.includes(id) || keep.has(id))
  } else {
    next.add(p.id)
    const toAdd = booked.filter(uid => !localSelected.value.includes(uid))
    localSelected.value = [...localSelected.value, ...toAdd]
  }
  checkedProjects.value = next
}

function selectBooked() {
  localSelected.value = bookedUserIds(props.project.id)
}

function selectBookedFuture() {
  localSelected.value = bookedUserIds(props.project.id, todayIso())
}

function selectAll() {
  localSelected.value = props.users.map((u: any) => u.id)
}

function deselectAll() {
  localSelected.value = []
}

function toggleUser(userId: number) {
  if (localSelected.value.includes(userId)) {
    localSelected.value = localSelected.value.filter(id => id !== userId)
  } else {
    localSelected.value = [...localSelected.value, userId]
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      @click.self="emit('cancel')"
    >
      <div class="bg-white rounded-lg shadow-xl flex flex-col w-[750px] max-h-[85vh]" @click.stop>

        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 class="text-lg font-semibold">
            <span :style="{ color: project.color }">{{ project.name }}</span>
            &nbsp;Personen wählen
          </h2>
          <button class="opacity-60 hover:opacity-100 transition-opacity" @click="emit('cancel')">
            <X :size="20" />
          </button>
        </div>

        <!-- Vorauswahl: Projekt-Checkboxen -->
        <div v-if="projectsWithBookings.length > 0" class="px-6 py-3 border-b shrink-0">
          <div class="flex flex-wrap gap-x-5 gap-y-2">
            <label
              v-for="p in projectsWithBookings"
              :key="p.id"
              class="flex items-center gap-1.5 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                :checked="isProjectChecked(p)"
                class="cursor-pointer"
                @change="toggleProject(p)"
              />
              <span class="text-sm font-medium" :style="{ color: p.color }">{{ p.name }}</span>
            </label>
          </div>
        </div>

        <!-- Aktionen + Suche -->
        <div class="flex items-center justify-between px-6 py-3 border-b shrink-0 gap-4">
          <div class="flex gap-4 text-sm">
            <button class="hover:underline" @click="selectBooked">Gebuchte</button>
            <button class="hover:underline" @click="selectBookedFuture">Gebucht ab heute</button>
            <button class="hover:underline" @click="selectAll">Alle</button>
            <button class="hover:underline" @click="deselectAll">Alle abwählen</button>
          </div>
          <div class="relative">
            <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              v-model="search"
              placeholder="Personen suchen"
              class="pl-8 h-8 border rounded-md text-sm w-48 pr-3 focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
          </div>
        </div>

        <!-- Personengrid -->
        <div class="grid grid-cols-4 gap-x-4 gap-y-1 px-6 py-4 overflow-y-auto flex-1">
          <label
            v-for="u in sortedFilteredUsers"
            :key="u.id"
            class="flex items-center gap-2 cursor-pointer py-0.5 select-none"
          >
            <input
              type="checkbox"
              :checked="localSelected.includes(u.id)"
              class="cursor-pointer shrink-0"
              @change="toggleUser(u.id)"
            />
            <span class="text-sm truncate">{{ u.firstname }} {{ u.lastname }} ({{ u.short }})</span>
          </label>
        </div>

        <!-- Footer -->
        <div class="flex justify-between px-6 py-4 border-t shrink-0">
          <Button variant="outline" @click="emit('cancel')">Abbrechen</Button>
          <Button
            class="bg-green-500 hover:bg-green-600 text-white"
            @click="emit('apply', [...localSelected])"
          >Anwenden</Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
